import { useMemo, useState, useCallback } from 'react';
import type { GroupedRowData } from '../DataGrid.types';
import type { AggregationConfig } from '../../utils/aggregations';
import { aggregate } from '../../utils/aggregations';

export interface UseGroupingOptions<T extends Record<string, unknown>> {
  data: T[];
  groupBy: (keyof T & string)[];
  aggregations: AggregationConfig;
}

export interface UseGroupingReturn<T extends Record<string, unknown>> {
  groupedData: GroupedRowData<T>[];
  expandedGroups: Set<string>;
  toggleGroup: (groupId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  flattenedRows: (GroupedRowData<T> | T)[];
}

export function useGrouping<T extends Record<string, unknown>>({
  data,
  groupBy,
  aggregations
}: UseGroupingOptions<T>): UseGroupingReturn<T> {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const groupedData = useMemo(() => {
    if (groupBy.length === 0) {
      return [];
    }

    function groupRecursive(
      items: T[],
      keys: (keyof T & string)[],
      level: number,
      parentKey: string
    ): GroupedRowData<T>[] {
      if (keys.length === 0 || items.length === 0) {
        return [];
      }

      const [currentKey, ...remainingKeys] = keys;
      const groups = new Map<unknown, T[]>();

      items.forEach(item => {
        const value = item[currentKey];
        if (!groups.has(value)) {
          groups.set(value, []);
        }
        groups.get(value)!.push(item);
      });

      return Array.from(groups.entries()).map(([value, groupItems]) => {
        const groupKey = parentKey ? `${parentKey}__${String(value)}` : String(value);

        const aggregatedValues: Record<string, number> = {};
        Object.entries(aggregations).forEach(([field, aggType]) => {
          const values = groupItems.map(item => item[field]);
          aggregatedValues[field] = aggregate(values, aggType);
        });

        const subRows = remainingKeys.length > 0
          ? groupRecursive(groupItems, remainingKeys, level + 1, groupKey)
          : [];

        return {
          isGroupHeader: true,
          groupKey,
          groupValue: value,
          level,
          rowCount: groupItems.length,
          aggregatedValues,
          originalRows: remainingKeys.length === 0 ? groupItems : undefined,
          subRows: subRows.length > 0 ? subRows : undefined
        };
      });
    }

    return groupRecursive(data, groupBy, 0, '');
  }, [data, groupBy, aggregations]);

  const flattenedRows = useMemo(() => {
    if (groupBy.length === 0) {
      return data;
    }

    const result: (GroupedRowData<T> | T)[] = [];

    function flatten(groups: GroupedRowData<T>[]) {
      groups.forEach(group => {
        result.push(group);

        if (expandedGroups.has(group.groupKey)) {
          if (group.subRows && group.subRows.length > 0) {
            flatten(group.subRows);
          } else if (group.originalRows) {
            result.push(...group.originalRows);
          }
        }
      });
    }

    flatten(groupedData);
    return result;
  }, [groupedData, expandedGroups, groupBy, data]);

  const expandAll = useCallback(() => {
    const allGroupKeys = new Set<string>();

    function collectKeys(groups: GroupedRowData<T>[]) {
      groups.forEach(group => {
        allGroupKeys.add(group.groupKey);
        if (group.subRows) {
          collectKeys(group.subRows);
        }
      });
    }

    collectKeys(groupedData);
    setExpandedGroups(allGroupKeys);
  }, [groupedData]);

  const collapseAll = useCallback(() => {
    setExpandedGroups(new Set());
  }, []);

  return {
    groupedData,
    expandedGroups,
    toggleGroup,
    expandAll,
    collapseAll,
    flattenedRows
  };
}
